import React, { useState, useRef, useEffect } from 'react'
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-react'

export default function MultiSelect({
    options = [],
    selectedIds = [],
    onChange,
    placeholder = 'Select options...',
    disabled = false,
    displayField = 'name',
    valueField = 'id',
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [dropUp, setDropUp] = useState(false)
    const dropdownRef = useRef(null)
    const inputRef = useRef(null)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
                setSearchTerm('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Flip upward when there's not enough space below
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            setDropUp(spaceBelow < 220)
        }
    }, [isOpen])

    const openDropdown = () => {
        if (!disabled) {
            setIsOpen(true)
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }

    const handleSelect = (id) => {
        const newSelectedIds = selectedIds.includes(id)
            ? selectedIds.filter(i => i !== id)
            : [...selectedIds, id]
        onChange(newSelectedIds)
        setSearchTerm('')
        inputRef.current?.focus()
    }

    const handleRemove = (id, e) => {
        e.stopPropagation()
        onChange(selectedIds.filter(i => i !== id))
    }

    const filteredOptions = options.filter(option =>
        option[displayField]?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const selectedOptions = options.filter(option => selectedIds.includes(option[valueField]))

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger: chips + inline search input */}
            <div
                onClick={openDropdown}
                className={`min-h-[38px] w-full bg-bg-light border border-border rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 cursor-text transition-colors ${
                    disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary'
                } ${isOpen ? 'border-primary ring-1 ring-primary/20' : ''}`}
            >
                {selectedOptions.map(option => (
                    <span
                        key={option[valueField]}
                        className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-md"
                    >
                        {option[displayField]}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => handleRemove(option[valueField], e)}
                                className="hover:text-primary/70 flex-shrink-0"
                            >
                                <IconX size={11} />
                            </button>
                        )}
                    </span>
                ))}

                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true) }}
                    onFocus={() => setIsOpen(true)}
                    placeholder={selectedOptions.length === 0 ? placeholder : ''}
                    disabled={disabled}
                    className="flex-1 min-w-[80px] bg-transparent text-sm text-text-primary placeholder:text-text-secondary outline-none"
                    onClick={(e) => e.stopPropagation()}
                />

                <IconChevronDown
                    size={16}
                    className={`ml-auto flex-shrink-0 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className={`absolute z-50 w-full bg-bg-dark border border-border rounded-lg shadow-lg overflow-hidden ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                    <div className="max-h-[160px] overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => {
                                const isSelected = selectedIds.includes(option[valueField])
                                return (
                                    <div
                                        key={option[valueField]}
                                        onMouseDown={(e) => { e.preventDefault(); handleSelect(option[valueField]) }}
                                        className={`flex items-center gap-2 px-3 py-[7px] cursor-pointer transition-colors ${
                                            isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-bg-light'
                                        }`}
                                    >
                                        <div className={`w-3 h-3 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                                            isSelected ? 'bg-primary border-primary' : 'border-border'
                                        }`}>
                                            {isSelected && <IconCheck size={10} className="text-white" />}
                                        </div>
                                        <span className="text-xs">{option[displayField]}</span>
                                        {option.fullName && (
                                            <span className="text-[11px] text-text-secondary ml-auto">{option.fullName}</span>
                                        )}
                                    </div>
                                )
                            })
                        ) : (
                            <div className="px-3 py-6 text-center text-text-secondary text-xs">
                                No options found
                            </div>
                        )}
                    </div>

                    {selectedOptions.length > 0 && (
                        <div className="px-3 py-2 border-t border-border flex items-center justify-between">
                            <span className="text-xs text-text-secondary">{selectedOptions.length} selected</span>
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); onChange([]) }}
                                className="text-xs text-danger hover:underline"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
